/**
 * 创作人之家人物波轮（02 开发文档 + 定稿原型 builder-dial.js 逐行移植）。
 * 算法 1:1 保留：圆柱弧面投影 / 滚轮惯性+吸附状态机 / 拖拽 flick / 键盘步进 / 端点回弹 /
 * 逐张倾角增量序列（40/56/68/78/86°）/ 景深虚化 / 视口双封顶缩放。
 * 接线替换：builders DTO → GET /public/builders；?lang= → useLocale()。
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import type { PublicBuilder } from '@chronospeak/contracts';
import type { Envelope } from '../lib/api-types';
import { http } from '../lib/http';
import { useLocale } from '../lib/locale';
import { copy } from '../lib/i18n';
import { DraftBadge, LocaleFallbackNotice } from '../components/bilingual';
import { SiteHeader } from '../components/SiteHeader';
import { DialSocial } from '../features/dial/DialSocial';

import placeholderAvatar from '../assets/people/placeholder.svg';

/* ---------------- 弧面常量（原型 §4 原值，不得改动） ---------------- */
// SCALE_MAX 2026-09-23 用户指示 1.9 → 1.5（卡片视觉过大；fitScale 双封顶公式不变，只收上限）
const SCALE_MAX = 1.5;
const ARC_STEP_DEGREES = 12;
const SETTLE_MS = 480;
const BOUNCE_MS = 420;
const VISUAL_LIMIT = 6.05;
const HIT_LIMIT = 5.15;
const TILT_STEPS = [0, 40, 56, 68, 78, 86];

/** 相对位姿换算（原型 getArcPosition 公式原样） */
function getArcPosition(position: number, radius: number): {
  x: number;
  z: number;
  rotate: number;
  opacity: number;
  blur: number;
} {
  const angle = position * ARC_STEP_DEGREES * (Math.PI / 180);
  const z = (Math.cos(angle) - 1) * radius;
  const distance = Math.abs(position);
  const lower = TILT_STEPS[Math.min(TILT_STEPS.length - 1, Math.floor(distance))] ?? 86;
  const upper = TILT_STEPS[Math.min(TILT_STEPS.length - 1, Math.floor(distance) + 1)] ?? 86;
  const edgeRotation = Math.min(86, lower + (upper - lower) * (distance % 1));
  return {
    x: Math.sin(angle) * radius,
    z,
    rotate: -Math.sign(position) * edgeRotation,
    opacity: distance <= HIT_LIMIT ? 1 : Math.max(0.55, 1 - (distance - HIT_LIMIT) * 0.45),
    blur: distance < 0.5 ? 0 : Math.min(10, Math.round((distance - 0.5) * 2.2 * 10) / 10),
  };
}

const SETTLE_EASE = `transform ${SETTLE_MS}ms cubic-bezier(0.22, 0.61, 0.36, 1), opacity ${SETTLE_MS}ms cubic-bezier(0.22, 0.61, 0.36, 1), filter ${SETTLE_MS}ms cubic-bezier(0.22, 0.61, 0.36, 1)`;

export function BuildersPage() {
  const { locale } = useLocale();
  const [builders, setBuilders] = useState<PublicBuilder[] | null>(null);
  const [meta, setMeta] = useState<Envelope<{ items: PublicBuilder[] }> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    let cancelled = false;
    http
      .get<Envelope<{ items: PublicBuilder[] }>>(`/public/builders?locale=${locale}`)
      .then((res) => {
        if (!cancelled) {
          setBuilders(res.data.items);
          setMeta(res);
        }
      })
      .catch(() => {
        if (!cancelled) setError(copy('common.loadFailed', locale));
      });
    return () => {
      cancelled = true;
    };
  }, [locale]);

  // 埋点：波轮页浏览
  useEffect(() => {
    if (!builders) return;
    void import('../lib/locale').then(({ track }) =>
      track('builders_dial_viewed', { locale, count: builders.length }),
    );
  }, [builders, locale]);

  if (error) {
    return (
      <main className='page builders-page'>
        <SiteHeader />
        <p role='alert' style={{ color: '#e8a9a0', padding: '24px' }}>
          <span aria-hidden='true'>✎ </span>
          {error}
        </p>
      </main>
    );
  }
  if (!builders || !meta) {
    return (
      <main className='page builders-page'>
        <SiteHeader />
        <p style={{ color: 'var(--muted)', padding: 24 }}>{copy('common.loading', locale)}</p>
      </main>
    );
  }
  return <Dial builders={builders} locale={locale} searchParams={searchParams} setSearchParams={setSearchParams} fallbackUsed={meta.fallbackUsed} />;
}

/* ---------------- 波轮主体（原型 dial() 状态机 1:1） ---------------- */

function Dial({
  builders,
  locale,
  searchParams,
  setSearchParams,
  fallbackUsed,
}: {
  builders: PublicBuilder[];
  locale: 'zh-CN' | 'en';
  searchParams: URLSearchParams;
  setSearchParams: ReturnType<typeof useSearchParams>[1];
  fallbackUsed: boolean;
}) {
  const stageRef = useRef<HTMLDivElement | null>(null);
  const cardsRef = useRef<(HTMLDivElement | null)[]>([]);
  const hitsRef = useRef<(HTMLButtonElement | null)[]>([]);

  const [activeIndex, setActiveIndex] = useState(() => resolveInitialIndex(builders, searchParams));

  // 运动状态（原型自由度 dragDistance + wheel 状态机）。
  // activeIndex 镜像：原型里 activeIndex 是同步变量，帧循环 while/边界判断直接读写；
  // React state 异步批处理会让 rAF 循环整段持有旧值（滚轮过冲回拉的根因，实现说明 §6.12）。
  // state 仅作 React 渲染镜像（intro 区/URL 同步），动画路径一律读 m.activeIndex。
  const motion = useRef({
    activeIndex: resolveInitialIndex(builders, searchParams),
    dragDistance: 0,
    isDragging: false,
    isSettling: false,
    isWheelScrolling: false,
    dragStart: { x: 0, time: 0 },
    settleTimer: null as number | null,
    wheelUnlockTimer: null as number | null,
    wheelFrame: null as number | null,
    wheelOffset: 0,
    wheelVelocity: 0,
    wheelTarget: null as number | null,
    scale: SCALE_MAX,
    baseCardH: 270,
  });

  const cardStep = useCallback(() => 300 * motion.current.scale, []);

  /** 位姿更新（原型 paint()：命令式直更，等价 React 调和但不销毁节点） */
  const paint = useCallback(() => {
    const m = motion.current;
    const live = m.isDragging || m.isWheelScrolling;
    const stage = stageRef.current;
    if (!stage) return;
    const cardStep = 300 * m.scale;
    builders.forEach((_, i) => {
      const continuous = i - m.activeIndex + m.dragDistance / cardStep;
      const visible = Math.abs(continuous) <= VISUAL_LIMIT;
      const card = cardsRef.current[i];
      if (card) {
        card.style.display = visible ? '' : 'none';
        if (visible) {
          const pos = getArcPosition(continuous, 1050 * m.scale);
          // scale 公式原型：1 - z / (1200 * SCALE)
          const scale = 1 - pos.z / (1200 * m.scale);
          card.style.transition = live ? 'none' : SETTLE_EASE;
          card.style.transform =
            'translate(-50%, -50%) translateX(' +
            pos.x.toFixed(2) +
            'px) translateZ(' +
            pos.z.toFixed(2) +
            'px) rotateY(' +
            pos.rotate.toFixed(2) +
            'deg) scale(' +
            scale.toFixed(4) +
            ')';
          card.style.opacity = pos.opacity.toFixed(3);
          card.style.filter = pos.blur ? `blur(${pos.blur}px)` : 'none';
          card.style.zIndex = String(20 - Math.round(Math.abs(continuous) * 2));
          card.classList.toggle('is-center', i === m.activeIndex && Math.abs(m.dragDistance) < cardStep * 0.5);
        }
      }
      const hit = hitsRef.current[i];
      if (hit) {
        const hitVisible = Math.abs(continuous) <= HIT_LIMIT;
        hit.style.display = hitVisible ? '' : 'none';
        if (hitVisible) {
          const pos = getArcPosition(continuous, 1050 * m.scale);
          const scale = 1 - pos.z / (1200 * m.scale);
          const isCenter = i === m.activeIndex && Math.abs(m.dragDistance) < cardStep * 0.5;
          hit.style.transform =
            'translate(-50%, -50%) translateX(' + pos.x.toFixed(2) + 'px) scale(' + scale.toFixed(4) + ')';
          hit.tabIndex = isCenter ? 0 : -1;
          if (isCenter) hit.setAttribute('aria-current', 'true');
          else hit.removeAttribute('aria-current');
          hit.setAttribute(
            'aria-label',
            isCenter
              ? (locale === 'zh-CN' ? '当前：' : 'Current: ') + (builders[i]?.name ?? '')
              : (locale === 'zh-CN' ? '切换到 ' : 'Switch to ') + (builders[i]?.name ?? ''),
          );
        }
      }
    });
  }, [builders, locale]);

  /** 视口双封顶缩放（原型 fitScale） */
  const fitScale = useCallback(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const m = motion.current;
    const byW = (stage.clientWidth - 90) / 230;
    const byH = (stage.clientHeight - 48) / m.baseCardH;
    m.scale = Math.max(0.62, Math.min(SCALE_MAX, byW, byH));
    stage.style.setProperty('--dial-scale', m.scale.toFixed(4));
  }, []);

  /** 索引推进单源（原型 activeIndex += 1 等价）：镜像先行（动画路径同步读）+ state 镜像供渲染 */
  const setActiveIndexSynced = useCallback((updater: (idx: number) => number) => {
    const m = motion.current;
    const next = updater(m.activeIndex);
    if (next === m.activeIndex) return;
    m.activeIndex = next;
    setActiveIndex(next);
  }, []);

  // onChange 联动：URL replaceState（原型 syncUrl）
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    next.set('builder', String(activeIndex));
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex]);

  // 索引变化 → paint + 切换埋点（埋点 method 沿原型 onChange 语义）
  useEffect(() => {
    paint();
    const current = builders[activeIndex];
    if (current) {
      void import('../lib/locale').then(({ track }) =>
        track('builder_switched', { builderSlug: current.slug, method: 'dial', locale }),
      );
    }
  }, [activeIndex, paint, builders, locale]);

  // 建卡后校准基准卡高 + resize 重算
  // 注意：本 effect 的 deps 含 paint（随 activeIndex 变化重跑）——不能在这里做
  // 定时器/rAF 清理：滚轮惯性循环运行中 setActiveIndex 会触发 cleanup 把
  // runWheelFrame 的帧杀掉，卡在半途（用户可见「只能滑一次」）。清理归卸载 effect。
  useEffect(() => {
    fitScale();
    const m = motion.current;
    const first = cardsRef.current[0];
    if (first) m.baseCardH = first.offsetHeight / m.scale || m.baseCardH;
    fitScale();
    paint();
    const onResize = () => {
      fitScale();
      paint();
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [fitScale, paint]);

  // 卸载清理（原型 pagehide 1:1：仅卸载时清，不在普通重渲染时杀运行中的动画）
  useEffect(() => {
    const m = motion.current;
    return () => {
      if (m.settleTimer !== null) window.clearTimeout(m.settleTimer);
      if (m.wheelUnlockTimer !== null) window.clearTimeout(m.wheelUnlockTimer);
      if (m.wheelFrame !== null) window.cancelAnimationFrame(m.wheelFrame);
    };
  }, []);

  /** 键盘/拖拽 flick 触发的步进（原型 changeCharacter） */
  const changeCharacter = useCallback(
    (direction: 1 | -1) => {
      const m = motion.current;
      if (builders.length < 2 || m.isSettling) return;
      const nextIndex = m.activeIndex + direction;
      if (nextIndex < 0 || nextIndex >= builders.length) {
        m.isSettling = true;
        m.dragDistance = 0;
        paint();
        m.settleTimer = window.setTimeout(() => (m.isSettling = false), BOUNCE_MS);
        return;
      }
      m.isSettling = true;
      m.dragDistance = direction * -cardStep();
      paint();
      m.settleTimer = window.setTimeout(() => {
        m.activeIndex = nextIndex;
        setActiveIndex(nextIndex);
        m.dragDistance = 0;
        m.isSettling = false;
        requestAnimationFrame(() => paintRef.current?.());
      }, SETTLE_MS);
    },
    [builders.length, cardStep, paint],
  );

  /** 点击直达（原型 changeToCharacter：清全部运动状态） */
  const changeToCharacter = useCallback(
    (nextIndex: number) => {
      const m = motion.current;
      if (nextIndex === m.activeIndex) return;
      if (nextIndex < 0 || nextIndex >= builders.length) return;
      if (m.wheelUnlockTimer !== null) window.clearTimeout(m.wheelUnlockTimer);
      if (m.wheelFrame !== null) window.cancelAnimationFrame(m.wheelFrame);
      m.wheelFrame = null;
      m.wheelOffset = 0;
      m.wheelVelocity = 0;
      m.wheelTarget = null;
      m.activeIndex = nextIndex;
      setActiveIndex(nextIndex);
      m.dragDistance = 0;
      m.isDragging = false;
      m.isWheelScrolling = false;
      m.isSettling = false;
      requestAnimationFrame(() => paintRef.current?.());
    },
    [builders.length],
  );

  /** 滚轮惯性/吸附动画帧循环（原型 runWheelFrame）。
   * activeIndex 走 motion.current 镜像（同步）；setActiveIndexSynced 仅在推进时镜像
   * 给 React。闭包稳定（deps 空），递归 rAF 不会持有过期状态。 */
  const runWheelFrameRef = useRef<() => void>(() => {});
  const runWheelFrame = useCallback(() => {
    const m = motion.current;
    const cardStep = 300 * m.scale;
    if (m.wheelTarget === null) {
      m.wheelOffset += m.wheelVelocity;
      m.wheelVelocity *= 0.88;

      const advance = (delta: number, dir: 1 | -1) => {
        m.wheelOffset += delta;
        setActiveIndexSynced((idx) => idx + dir);
      };
      while (m.wheelOffset <= -cardStep && m.activeIndex < builders.length - 1) {
        advance(cardStep, 1);
      }
      while (m.wheelOffset >= cardStep && m.activeIndex > 0) {
        advance(-cardStep, -1);
      }
      if (
        (m.activeIndex === builders.length - 1 && m.wheelOffset < 0) ||
        (m.activeIndex === 0 && m.wheelOffset > 0)
      ) {
        m.wheelOffset = 0;
        m.wheelVelocity = 0;
      }
    } else {
      const distanceToTarget = m.wheelTarget - m.wheelOffset;
      m.wheelOffset += distanceToTarget * 0.2;
      if (Math.abs(distanceToTarget) < 0.6) {
        const steps = Math.round(-m.wheelTarget / cardStep);
        setActiveIndexSynced((idx) => Math.max(0, Math.min(builders.length - 1, idx + steps)));
        m.dragDistance = 0;
        m.isWheelScrolling = false;
        m.wheelOffset = 0;
        m.wheelVelocity = 0;
        m.wheelTarget = null;
        m.wheelFrame = null;
        requestAnimationFrame(() => paintRef.current?.());
        return;
      }
    }
    m.dragDistance = m.wheelOffset;
    paintRef.current?.();
    m.wheelFrame = window.requestAnimationFrame(runWheelFrameRef.current);
  }, [builders.length, setActiveIndexSynced]);
  useEffect(() => {
    runWheelFrameRef.current = runWheelFrame;
  }, [runWheelFrame]);

  // paint 的 ref 形态（供定时器/帧循环取最新闭包）
  const paintRef = useRef(paint);
  useEffect(() => {
    paintRef.current = paint;
    paint();
  }, [paint]);

  /** 滚轮 handler（原型 handleWheel：非被动 + 110ms 后吸附） */
  const onWheel = useCallback(
    (event: React.WheelEvent) => {
      event.preventDefault();
      const m = motion.current;
      const cardStep = 300 * m.scale;
      if (m.wheelUnlockTimer !== null) window.clearTimeout(m.wheelUnlockTimer);
      m.wheelUnlockTimer = window.setTimeout(() => {
        const requestedSteps = Math.round(-m.wheelOffset / cardStep);
        const availableBackwardSteps = m.activeIndex;
        const availableForwardSteps = builders.length - 1 - m.activeIndex;
        const boundedSteps = Math.max(-availableBackwardSteps, Math.min(availableForwardSteps, requestedSteps));
        m.wheelTarget = -boundedSteps * cardStep;
        m.wheelVelocity = 0;
        if (m.wheelFrame === null) runWheelFrame();
      }, 110);
      const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
      if (Math.abs(delta) < 1 || m.isSettling || m.isDragging) return;
      m.wheelTarget = null;
      m.wheelVelocity = Math.max(-180, Math.min(180, m.wheelVelocity - delta * 0.35));
      m.isWheelScrolling = true;
      if (m.wheelFrame === null) runWheelFrame();
    },
    [builders.length, runWheelFrame],
  );

  // 拖拽（pointer events；touch-action: pan-y 由 CSS 提供）
  const onPointerDown = (e: React.PointerEvent) => {
    const m = motion.current;
    if (m.isSettling || m.isWheelScrolling) return;
    m.dragStart = { x: e.clientX, time: performance.now() };
    m.isDragging = true;
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const m = motion.current;
    if (!m.isDragging || m.isSettling) return;
    const distance = e.clientX - m.dragStart.x;
    const el = e.currentTarget as HTMLElement;
    if (Math.abs(distance) >= 4 && !el.hasPointerCapture(e.pointerId)) {
      el.setPointerCapture(e.pointerId);
    }
    m.dragDistance = distance;
    paint();
  };
  const onPointerUp = (e: React.PointerEvent) => {
    const m = motion.current;
    if (!m.isDragging) return;
    const distance = e.clientX - m.dragStart.x;
    const elapsed = Math.max(1, performance.now() - m.dragStart.time);
    const velocity = Math.abs(distance) / elapsed;
    m.isDragging = false;
    if (Math.abs(distance) < 4) {
      m.dragDistance = 0;
      paint();
      return;
    }
    if (Math.abs(distance) > cardStep() * 0.2 || velocity > 0.4) changeCharacter(distance < 0 ? 1 : -1);
    else {
      m.isSettling = true;
      m.dragDistance = 0;
      paint();
      m.settleTimer = window.setTimeout(() => (m.isSettling = false), BOUNCE_MS);
    }
  };
  const onPointerCancel = () => {
    const m = motion.current;
    m.isDragging = false;
    m.dragDistance = 0;
    paint();
  };

  // 键盘 ←/→（window 级；输入控件守卫）
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      const t = event.target;
      if (t instanceof HTMLElement && (t.matches('input, textarea, select') || t.isContentEditable)) return;
      event.preventDefault();
      changeCharacter(event.key === 'ArrowRight' ? 1 : -1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [changeCharacter]);

  const b = builders[activeIndex];

  return (
    <main className='page builders-page'>
      <SiteHeader />
      {/* 原型此页无 H1（axe page-has-heading-one）——补视觉隐藏 H1，视觉 1:1 语义达标（M4 a11y） */}
      <h1 className='visually-hidden'>{locale === 'zh-CN' ? '创作人之家' : 'Builders’ House'}</h1>
      <section
        className='dial-stage'
        ref={stageRef}
        aria-label={locale === 'zh-CN' ? 'Builder 人物波轮' : 'Builder dial'}
        onWheel={onWheel}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
      >
        <div className='dial-viewport'>
          <div className='dial-glow' aria-hidden='true' />
          <div className='dial-cards'>
            {builders.map((builder, i) => (
              <div key={builder.slug} className='dial-card' ref={(el) => void (cardsRef.current[i] = el)} aria-hidden='true'>
                {builder.photoUrl ? (
                  // 缩略图优先（OSS 400 宽 WebP，2026-09-23 性能：7.5MB 原图→32KB）；失败/缺省回退原图。
                  // lazy：两侧不可见卡不抢带宽；async：解码不阻塞滑动帧。
                  // onError 用 dataset 标记防循环回退（img.src 是绝对 URL、photoUrl 是相对路径，
                  // 字符串比较恒不等 → 会无限重设 src 重试，本地 1.6 万 TIME_WAIT 实锤过这个坑）。
                  <img
                    className='dial-photo'
                    src={builder.photoThumbUrl ?? builder.photoUrl}
                    alt=''
                    loading='lazy'
                    decoding='async'
                    onError={(e) => {
                      const img = e.currentTarget;
                      if (img.dataset.fallback) return;
                      if (!builder.photoUrl) return;
                      img.dataset.fallback = '1';
                      img.src = builder.photoUrl;
                    }}
                  />
                ) : (
                  <div className='dial-poster'>
                    <PlaceholderAvatar seed={i + 1} />
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className='dial-hits'>
            {builders.map((builder, i) => (
              <button
                key={builder.slug}
                type='button'
                className='dial-hit'
                ref={(el) => void (hitsRef.current[i] = el)}
                onClick={() => changeToCharacter(i)}
              />
            ))}
          </div>
        </div>
      </section>
      <section className='dial-intro' aria-live='polite'>
        {b && (
          <div className='dial-intro-grid'>
            <div className='dial-intro-id'>
              <p className='eyebrow'>
                {b.role}
                {b.isCoach && <span className='coach-tag'>{locale === 'zh-CN' ? 'Coach' : 'Coach'}</span>}
                <DraftBadge text={b.name} />
              </p>
              <h2>{b.name}</h2>
            </div>
            <div className='dial-intro-copy'>
              {/* 宣言仅换行（一行宣言加粗语法无意义——实现说明 B2 克制约定） */}
              <p className='dial-statement' style={{ whiteSpace: 'pre-line' }}>{b.statement}</p>
              {/* 简介富文本（2026-09-25）：服务端 md→html+净化（bioHtml）；缺省回退纯文本（存量兼容） */}
              {b.bioHtml ? (
                <div className='dial-bio dial-bio-rich' dangerouslySetInnerHTML={{ __html: b.bioHtml }} />
              ) : (
                <p className='dial-bio'>{b.bio}</p>
              )}
              {/* 社媒跳转（2026-09-23 FR-03 增补）：bio 下方 icon 横排；空数据不渲染整行 */}
              <DialSocial links={b.socialLinks ?? []} name={b.name} locale={locale} />
            </div>
          </div>
        )}
        {fallbackUsed && <LocaleFallbackNotice fallbackUsed />}
      </section>
      {/* noscript 静态人物索引兜底（02 开发清单） */}
      <noscript>
        <nav className='builder-index'>
          {builders.map((x, i) => `${String(i + 1).padStart(2, '0')} · ${x.name}`).join('　')}
        </nav>
      </noscript>
      <img src={placeholderAvatar} alt='' style={{ display: 'none' }} />
    </main>
  );
}

/** 占位插画（原型 avatar(n)：编号占位——工程化以 SVG 数据占位回退，无照片 Builder 消费） */
function PlaceholderAvatar({ seed }: { seed: number }) {
  return (
    <div className={`avatar avatar-${seed}`} aria-hidden='true'>
      <span>{String(seed).padStart(2, '0')}</span>
    </div>
  );
}

/** ?builder= 深链解析（原型 builderIndexFromQuery：slug/0 基序号/无效回落首位） */
function resolveInitialIndex(builders: PublicBuilder[], searchParams: URLSearchParams): number {
  const raw = searchParams.get('builder');
  if (raw === null || raw === '') return 0;
  const slugIndex = builders.findIndex((x) => x.slug === raw);
  if (slugIndex >= 0) return slugIndex;
  const n = Number(raw);
  if (!Number.isInteger(n) || n < 0 || n >= builders.length) return 0;
  return n;
}
